import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Copy, Trash2, AlertTriangle } from "lucide-react";
import { api, errMsg, usd, photoUrl } from "@/lib/api";
import { PageTitle, Btn } from "./ResourcePage";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const STATUS = { borrador: "bg-slate-100", en_cola: "bg-amber-100 text-amber-700", renderizando: "bg-amber-100 text-amber-700", listo: "bg-emerald-100 text-emerald-700", aprobado: "bg-emerald-600 text-white", error: "bg-red-100 text-red-700" };

function Library() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const load = () => api.get("/admin/video/projects").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const dup = async (p) => { const r = await api.post(`/admin/video/projects/${p.id}/duplicate`); nav(`/admin/video/${r.data.id}`); };
  const del = async (p) => { if (window.confirm("¿Eliminar proyecto de video?")) { await api.delete(`/admin/video/projects/${p.id}`); load(); } };
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="video-library">
      {items.map((p) => (
        <div key={p.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`video-project-${p.id}`}>
          <Link to={`/admin/video/${p.id}`} className="block aspect-video bg-slate-900 relative">
            {p.scenes?.[0] && <img src={photoUrl(p.scenes[0].photo)} alt="" className="w-full h-full object-cover opacity-80" />}
            <span className={`absolute top-2 left-2 text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS[p.status] || "bg-slate-100"}`}>{p.status}</span>
          </Link>
          <div className="p-4 space-y-1">
            <p className="font-semibold truncate">{p.name}</p>
            <p className="text-xs text-slate-500">{p.format} · {p.duration}s · {p.template} · {p.versions?.length || 0} versiones</p>
            <p className="text-xs text-slate-400">{p.created_at?.slice(0, 10)} · {p.created_by}</p>
            <div className="flex gap-3 pt-2 text-sm">
              <Link to={`/admin/video/${p.id}`} className="font-semibold text-[#FF3333]">Abrir editor</Link>
              <button onClick={() => dup(p)} className="text-slate-500 hover:text-slate-900" title="Duplicar" data-testid={`video-dup-${p.id}`}><Copy size={15} /></button>
              <button onClick={() => del(p)} className="text-slate-500 hover:text-red-600" title="Eliminar" data-testid={`video-del-${p.id}`}><Trash2 size={15} /></button>
            </div>
          </div>
        </div>
      ))}
      {!items.length && <p className="text-slate-500" data-testid="video-library-empty">Aún no hay videos. Crea uno desde una propiedad o con el botón "Nuevo video".</p>}
    </div>
  );
}

function Costs() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/admin/video/costs").then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!c) return <p>Cargando...</p>;
  const Item = ({ l, v }) => <div className="bg-white rounded-2xl border border-slate-200 p-5"><p className="text-xs uppercase text-slate-500 font-semibold">{l}</p><p className="font-mono-num text-xl mt-2">{v}</p></div>;
  return (
    <div className="space-y-6" data-testid="video-costs">
      {c.alert && <div className="flex gap-2 items-center bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 text-sm"><AlertTriangle size={16} />Has superado el 80% del presupuesto mensual de IA.</div>}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-6">
        <Item l="Videos generados" v={c.videos_generated} /><Item l="Fallidos" v={c.failed} /><Item l="Costo por video" v={usd(c.cost_per_video)} />
        <Item l="Consumo del mes" v={usd(c.month_spent)} /><Item l="Presupuesto mensual" v={usd(c.monthly_budget)} /><Item l="Límite por usuario/mes" v={c.limit_per_user} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5"><h4 className="font-semibold mb-3">Proveedores</h4>
          {c.providers.map((p) => <div key={p.id} className="flex justify-between text-sm py-2 border-b last:border-0"><span>{p.name}</span><span className={p.available ? "text-emerald-600" : "text-slate-400"}>{p.available ? "Activo" : "Sin clave API"} · {usd(c.by_provider[p.id] || 0)}</span></div>)}
          {Object.entries(c.by_provider).filter(([k]) => !c.providers.find((p) => p.id === k)).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-2"><span>{k}</span><span>{usd(v)}</span></div>)}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 max-h-80 overflow-y-auto"><h4 className="font-semibold mb-3">Historial</h4>
          {c.history.map((g) => <div key={g.id} className="flex justify-between text-xs py-1.5 border-b last:border-0"><span>{g.created_at.slice(0, 16).replace("T", " ")} · {g.kind} · {g.user_email}</span><span>{g.status} · {usd(g.cost_usd)}</span></div>)}
        </div>
      </div>
    </div>
  );
}

export default function VideoStudio() {
  return (
    <div data-testid="video-studio-page">
      <PageTitle title="SoloRenta AI Video Studio" subtitle="Videos para Reels, Stories, TikTok y Shorts a partir de las fotos reales del catálogo">
        <Link to="/admin/video/new"><Btn data-testid="video-new-button"><Plus size={15} />Nuevo video</Btn></Link>
      </PageTitle>
      <Tabs defaultValue="library">
        <TabsList><TabsTrigger value="library" data-testid="video-tab-library">Biblioteca</TabsTrigger><TabsTrigger value="costs" data-testid="video-tab-costs">Consumo y costos</TabsTrigger></TabsList>
        <TabsContent value="library" className="mt-6"><Library /></TabsContent>
        <TabsContent value="costs" className="mt-6"><Costs /></TabsContent>
      </Tabs>
    </div>
  );
}
